/**
 * 项目/画布相关类型，供 stores、utils 等公共层使用
 */
import type {
  LlmAgentAgentV1Project,
  LlmAgentAgentV1VideoGroupContent,
  ViduV1Canvas,
  ViduV1Container,
  ViduV1Content,
  ViduV1GetViralGrowthInfoReply,
} from '@/app/services/vidu';
import type { AgentVideoRatioType } from './agent-chat';

export type ViduContainerType = ViduV1Container;

/** 项目详情 */
export type ProjectDetailType = LlmAgentAgentV1Project;
/** 画布详情 */
export type CanvasDetailType = Omit<ViduV1Canvas, 'updated_at' | 'containers'>;

/** 节点类型：inputNode 输入节点，creationVideoNode 视频产物节点，creationScriptNode 脚本节点 */
export type CanvasNodeType = 'inputNode' | 'creationVideoNode' | 'creationScriptNode';

export type NodeType = 'input' | 'aiCreation';
export type ContainerType = Exclude<ViduV1Container['type'], 'unspecified' | undefined>;
export type ContainerStatus = Exclude<ViduV1Container['status'], 'unspecified' | undefined>;
export type ContentTag = Exclude<ViduV1Content['tags'], 'unspecified' | undefined>;
export type VideoGroupContent = LlmAgentAgentV1VideoGroupContent;

export type NodeData = {
  created_at: string; // 创建时间
  nodeType: NodeType; // 产物类型，input用户输入，aiCreationAI创作
  container_id: string; // 容器id
  container_type: ContainerType; // 容器类型，text文本，image图片，video视频，script脚本
  content_id: string; // 内容id
  status: ContainerStatus; // 状态
  parent_ids?: string[]; // 父节点id

  name: string; // 节点名称
  tags: ContentTag; // 节点资源标签

  text?: string;
  images_url?: string;
  videos?: {
    url?: string;
    cover_url?: string;
    duration?: number;
    ratio?: AgentVideoRatioType;
    bgm_url?: string;
    bgm_volume?: number;
    tts_url?: string;
    tts_volume?: number;
    input_text?: string;
    index?: number;
    reference_image_id?: string[];
    full_narration?: string;
    voice_id?: string;
    /** 带水印的视频 URL */
    watermark_url?: string;
  };
  script?: string;
  video_group?: VideoGroupContent;
  [key: string]: unknown;
};

/**
 * 轻量级的 ReactFlow UI 节点
 * 只存储 UI 状态和业务数据的 ID 引用
 */
export interface CanvasUINode {
  id: string; // UI 节点的唯一 ID
  type: CanvasNodeType; // 节点类型
  position: {
    x: number; // 节点x坐标
    y: number; // 节点y坐标
  };
  data: {
    // 关联的业务数据 ID 列表（支持单个或多个）
    businessDataIds: string[];
    // UI 相关状态
    parentIds?: string; // 父节点 ID（用于布局）
    [key: string]: unknown; // 添加索引签名以满足 ReactFlow 约束
  };
  measured?: {
    width: number; // 节点宽度
    height: number; // 节点高度
  };
  selected?: boolean;
  [key: string]: unknown; // 添加索引签名以满足 ReactFlow 约束
}

/**
 * 节点业务数据（不包含位置等 UI 状态）
 */
export interface NodeBusinessData {
  id: string; // 对应 container.id
  name: string;
  type: CanvasNodeType;
  data: NodeData;
}

export type AgentClipGroup = {
  /** 组索引 */
  clipGroupIndex: number;
  /** 当前选中的分镜 ID */
  currentClipId: string;
  /** 当前选中的分镜是否加载完 */
  isCurrentClipLoaded: boolean;
  /** 当前组下分镜列表 */
  clipIds: string[];
};

/**
/**
 * 视频片段类型定义
 */
export interface VideoClip {
  /** 片段唯一标识 */
  id: string;
  /** 视频 URL */
  url: string;
  /** 封面 URL */
  cover_url: string;
  /** 时长 */
  duration: number;
  /** 输入文本 */
  input_text: string;
  /** 状态 */
  status: ContainerStatus;
  /** 创建时间 */
  created_at: string;
  /** 宽高比 */
  ratio: AgentVideoRatioType;
  /** 引用图片 ID */
  reference_image_id: string[];
}

/**
 * 积分信息
 */
export interface CreditCostInfo {
  costCredits?: number;
  originalCredits?: number;
}

/**
 * 积分消耗 Map 类型
 * key: `${costType}_${duration}`，例如 "video_generate_15"
 */
export type CreditCostMap = Map<string, CreditCostInfo>;

/**
 * 资源查看项（用于 ViewResourceDialog 等）
 */
export type ViewResourceItem = {
  id: string;
  name?: string;
  type: 'video' | 'image';
  /**
   * 视频 & 图片 链接
   */
  url: string;
  /**
   * 封面 & 缩略图
   */
  poster: string;
  /**
   * 状态
   */
  status?: ContainerStatus;
  /**
   * 带水印的视频 URL（仅视频类型）
   */
  watermark_url?: string;
  /** 媒体实际宽度（用于 MediaWithWatermark 计算水印位置） */
  width?: number;
  /** 媒体实际高度（用于 MediaWithWatermark 计算水印位置） */
  height?: number;
};

/**
 * 积分消耗类型枚举
 */
export enum COST_TYPE {
  /** 视频生成 */
  VIDEO_GENERATE = 'video_generate',
  /** 分镜生成 */
  CLIP_GENERATE = 'clip_generate',
  /** 脚本生成 */
  // SCRIPT_GENERATE = 'script_generate',
  /** 视频合并 */
  VIDEO_MERGE = 'video_merge',
  /** 脚本生成带视频 */
  // SCRIPT_GENERATE_WITH_VIDEO = 'script_generate_with_video',
  /** 第一次视频生成 */
  FIRST_VIDEO_GENERATE = 'first_video_generate',
  /** 0积分生成成片 */
  ZERO_CREDITS_VIDEO_GENERATE = 'zero_credits_video_generate',
}

export type AgentInviteInfoType = ViduV1GetViralGrowthInfoReply & {
  max_friends_invited_count: number;
  has_free_project_count: number;
  free_projects_quota: number;
  status: 'invited' | 'not_invited';
};
